import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { v4 as uuidv4 } from "uuid";
import UnStyledImage from "./image";
import DuotoneSvgFilter from "./duotone-svg-filter";
import { backgroundImageCss } from "../utils/background-image";

const CoverWrapper = styled.div.attrs(
  ({ align, minHeight, minHeightUnit }) => ({
    align: align,
    minHeight: minHeight || 50,
    minHeightUnit: minHeightUnit || "vh",
  })
)`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  flex-direction: column;
  position: relative;
  width: 100%;
  max-width: ${({ align }) => (align === "full" ? "none" : "1180px")};
  min-height: ${({ minHeight, minHeightUnit }) =>
    `${minHeight}${minHeightUnit}`};
  margin: 0 auto;
`;

const CoverOverlay = styled.div.attrs(({ $dimRatio, $backgroundColor }) => ({
  $opacity: $dimRatio ? $dimRatio / 100 : 0,
  $backgroundColor: $backgroundColor || "#000",
}))`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: #2e2e2e;
  opacity: ${({ $opacity }) => $opacity};
`;
const Image = styled(UnStyledImage).attrs(({ $minHeight, $minHeightUnit }) => ({
  $minHeight: $minHeight || 50,
  $minHeightUnit: $minHeightUnit || "vh",
}))`
  width: 100%;
  height: ${({ $minHeight, $minHeightUnit }) =>
    `${$minHeight}${$minHeightUnit}`};
  position: absolute;
  top: 0;
  left: 0;
`;
const ImgParallax = styled.div.attrs(
  ({
    $bgImgCss,
    $duotoneId,
    $minHeight,
    $minHeightUnit,
    $hasParallax,
    $imgSrc,
    $imgPos,
  }) => ({
    $bgImgCss: $bgImgCss ?? "",
    $duotoneId: $duotoneId,
    $minHeight: $minHeight || 50,
    $minHeightUnit: $minHeightUnit || "vh",
    $hasParallax: $hasParallax ? "fixed" : "unset",
    $imgSrc: $imgSrc,
    $imgPos: $imgPos || "50% 50%",
  })
)`
  width: 100%;
  height: ${({ $minHeight, $minHeightUnit }) =>
    `${$minHeight}${$minHeightUnit}`};
  position: absolute;
  top: 0;
  left: 0;
  background-image: ${({ $imgSrc }) => `url(${$imgSrc})`};
  background-attachment: ${({ $hasParallax }) => $hasParallax};
  background-size: cover;
  background-repeat: no-repeat;
  ${({ $duotoneId }) => ($duotoneId ? `filter: url(#${$duotoneId});` : "")};
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    background-position: ${({ $imgPos }) => $imgPos};
    @media not all and (min-resolution: 0.001dpcm) {
      @supports (-webkit-appearance: none) {
        background-attachment: unset;
      }
    }
  }
  ${({ $bgImgCss }) => $bgImgCss}
`;
const InnerBlock = styled.div.attrs(
  ({ minHeight, minHeightUnit, contentPosition }) => ({
    minHeight: minHeight || 50,
    minHeightUnit: minHeightUnit || "vh",
    contentPosition: contentPosition || "center",
  })
)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: ${({ contentPosition }) =>
    contentPosition.includes("bottom")
      ? "flex-end"
      : contentPosition.includes("top")
        ? "flex-start"
        : "center"};
  align-items: ${({ contentPosition }) =>
    contentPosition.includes("left")
      ? "flex-start"
      : contentPosition.includes("right")
        ? "flex-end"
        : "center"};
  flex-basis: ${({ minHeight, minHeightUnit }) =>
    `${minHeight}${minHeightUnit}`};
  flex-grow: 1;
  flex-shrink: 1;
  min-height: ${({ minHeight, minHeightUnit }) =>
    `${minHeight}${minHeightUnit}`};
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    padding: 1rem;
  }

  h1 {
    color: white;
  }
`;

const Cover = ({
  media,
  minHeight,
  minHeightUnit,
  align,
  dimRatio,
  duotoneColorValues,
  overlayColor,
  children,
  contentPosition,
  hasParallax,
  className,
  aboveFold,
}) => {
  const [duotoneId] = React.useState(() => {
    if (duotoneColorValues) {
      return uuidv4();
    }
    return null;
  });

  const [bgImgCss] = React.useState(() => {
    const image = media?.localFile?.childImageSharp?.gatsbyImageData;
    const fluid = {
      aspectRatio: image?.width / image?.height,
      srcSetWebp: image?.images?.sources?.[0]?.srcSet,
    };
    return backgroundImageCss(
      fluid.aspectRatio && fluid.srcSetWebp ? fluid : {}
    );
  });
  const imgSrc =
    media?.localFile?.publicURL ||
    media?.sourceUrl ||
    media?.mediaItemUrl ||
    "";

  return (
    <>
      <CoverWrapper
        minHeight={minHeight}
        minHeightUnit={minHeightUnit}
        align={align}
        className={`cover-bread ${className}`}
      >
        {duotoneId && (
          <DuotoneSvgFilter id={duotoneId} values={duotoneColorValues} />
        )}
        {hasParallax && imgSrc ? (
          <ImgParallax
            $imgSrc={imgSrc}
            $imgPos={media?.focalPoint?.objectPosition}
            $bgImgCss={bgImgCss}
            $duotoneId={duotoneId}
            $minHeight={minHeight}
            $minHeightUnit={minHeightUnit}
            width={1920}
            $hasParallax={hasParallax}
          />
        ) : (
          <Image
            image={media}
            src={imgSrc}
            $minHeight={minHeight}
            $minHeightUnit={minHeightUnit}
            imgStyle={{ filter: duotoneId ? `url(#${duotoneId})` : undefined }}
            width={1920}
            loading={aboveFold ? "eager" : undefined}
            fetchpriority={aboveFold ? "high" : undefined}
          />
        )}
        <CoverOverlay $backgroundColor={overlayColor} $dimRatio={dimRatio} />
        <InnerBlock
          className="cover-inner-bread"
          minHeight={minHeight}
          minHeightUnit={minHeightUnit}
          contentPosition={contentPosition}
        >
          {children}
        </InnerBlock>
      </CoverWrapper>
    </>
  );
};

Cover.propTypes = {
  duotoneColorValues: PropTypes.shape({
    r: PropTypes.string,
    g: PropTypes.string,
    b: PropTypes.string,
    a: PropTypes.string,
  }),
  media: PropTypes.object,
  minHeight: PropTypes.number,
  minHeightUnit: PropTypes.string,
  align: PropTypes.string,
  children: PropTypes.node,
  dimRatio: PropTypes.number,
  overlayColor: PropTypes.string,
  contentPosition: PropTypes.string,
  hasParallax: PropTypes.bool,
  className: PropTypes.string,
  aboveFold: PropTypes.bool,
};

Cover.defaultProps = {
  media: undefined,
};

export { Cover, InnerBlock };
